/**
 * Deal Briefs admin landing — list of all generated AI deal briefs,
 * sortable by match score / recency, click-through to MatchDetailPage.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealBriefService from '../services/dealBriefService';
import { DealBrief } from '../types/dealBrief';

const DealBriefsPage: React.FC = () => {
  const [briefs, setBriefs] = useState<DealBrief[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [minScore, setMinScore] = useState<number>(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await dealBriefService.listBriefs({
        size: 100,
        min_match_score: minScore || undefined,
      });
      if (result.data) setBriefs(result.data.briefs);
      setLoading(false);
    };
    load();
  }, [minScore]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-blue-600" />
              Deal Briefs
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              AI-generated strategic briefs per match — auto-produced for
              scores above the threshold, and on-demand from any match detail
              page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Min match score:</label>
            <select
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
            >
              <option value={0}>Any</option>
              <option value={50}>≥ 50</option>
              <option value={70}>≥ 70</option>
              <option value={80}>≥ 80</option>
              <option value={90}>≥ 90</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading briefs...
          </div>
        ) : briefs.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-800 mb-1">No deal briefs yet</p>
            <p className="text-sm text-gray-500">
              High-scoring matches will auto-generate briefs here, or open any
              match and click "Generate Deal Brief".
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Match</th>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3">Generated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {briefs.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/matches/${b.match_id}`}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Match #{b.match_id}
                      </Link>
                      {b.brief_json?.executive_summary && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2 max-w-2xl">
                          {b.brief_json.executive_summary.slice(0, 200)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      v{b.version}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {b.model_used || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {b.generated_at
                        ? new Date(b.generated_at).toLocaleString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/matches/${b.match_id}`}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DealBriefsPage;
